import { InfoCircleOutlined } from "@ant-design/icons"
import { DatePicker, type DatePickerProps, Form, Input, InputNumber, Switch, Tooltip } from "antd"
import locale from "antd/es/date-picker/locale/pt_BR"
import { MaskedInput } from "antd-mask-input"
import { type MaskType } from "antd-mask-input/build/main/lib/MaskedInput"
import dayjs, { type Dayjs } from "dayjs"
import React, { useEffect, useState } from "react"

import { onRequiredTypeChange, removePunctuation, toLicensePlate } from "../../../helpers/format"
import { type ApplicationState } from "../../../store"
import KintoButton, { KintoButtonThemes } from "../../KintoButton"

export interface CarInfoData {
  returnDate: Dayjs
  licensePlate: string
  model: string
  carKm: number
  hasDamageOrMissingItems: boolean
}

type OwnProps = {
  onPlateChange?: (plate: string) => void
  afterFinish?: (formData: CarInfoData) => void
  handleReturn?: () => void
  handleCarProblemChange?: (value: boolean) => void
  defaults?: ApplicationState["onefleetdevolucao"]
}

const CarInfo = (props: OwnProps): React.ReactElement => {
  const [formData] = Form.useForm()
  const [plateChanged, setPlateChanged] = useState<boolean>(false)

  const plateMask = React.useMemo<MaskType>(() => {
    return [{ mask: "aaa-0*00" }]
  }, [])

  function handleFormFinish () {
    if (props.afterFinish) {
      const {
        carKm,
        licensePlate,
        model,
        returnDate,
        hasDamageOrMissingItems
      } = formData.getFieldsValue() as CarInfoData

      props.afterFinish({
        carKm,
        licensePlate: toLicensePlate(licensePlate),
        model,
        returnDate,
        hasDamageOrMissingItems
      })
    }
  }

  useEffect(() => {
    if (props.defaults?.plateInfo?.data && plateChanged) {
      const {
        model
      } = props.defaults.plateInfo.data

      formData.setFieldsValue({
        model
      })

      setPlateChanged(false)
    }
  }, [props.defaults?.plateInfo?.data])

  const range = (start: number, end: number) => {
    const result = []
    for (let i = start; i < end; i++) {
      result.push(i)
    }
    return result
  }

  const disabledDate: DatePickerProps["disabledDate"] = (current) => {
    return current > dayjs()
  }

  const disabledDateTime: any = (current: any) => {
    const isToday = dayjs().diff(current, "d") === 0
    return {
      disabledHours: () => isToday ? range(dayjs().add(1, "h").hour(), 24) : [],
      disabledMinutes: () => isToday ? range(dayjs().add(1, "m").minute(), 60) : []
    }
  }

  return (
    <Form
      onFinish={handleFormFinish}
      form={formData}
      layout="vertical"
      initialValues={props.defaults?.data.car}
      requiredMark={onRequiredTypeChange}
    >
      <Form.ErrorList />
      <Form.Item
        name="licensePlate"
        label="Placa do veículo"
        rules={[
          { required: true, message: "Campo obrigatório!" }
        ]}
      >
        <MaskedInput
          size="large"
          placeholder="Placa do veículo"
          prefix={props.defaults?.plateInfo?.error && <Tooltip title={`Preenchimento manual necessário\nMotivo: ${props.defaults?.plateInfo?.message}`}><InfoCircleOutlined /></Tooltip>}
          mask={plateMask}
          onBlur={(e) => {
            if (props.onPlateChange) props.onPlateChange(removePunctuation(e.target.value).toUpperCase())
            setPlateChanged(true)
          }}
        />
      </Form.Item>

      <Form.Item
        name="model"
        label="Modelo do veículo"
        rules={[
          { required: true, message: "Campo obrigatório!" }
        ]}
      >
        <Input
          size="large"
          disabled={Boolean(props.defaults?.plateInfo?.loading)}
          placeholder="Modelo do veículo"
        />
      </Form.Item>

      <Form.Item
        name="carKm"
        label="Km atual do veículo"
        rules={[
          { required: true, message: "Campo obrigatório!" }
        ]}
      >
        <InputNumber
          size="large"
          placeholder="Km do veículo"
          style={{ width: "100%" }}
          min={0}
          max={999999}
        />
      </Form.Item>

      <Form.Item
        name="returnDate"
        label="Data de devolução"
        rules={[
          { required: true, message: "Campo obrigatório!" }
        ]}
      >
        <DatePicker
          showTime
          showSecond={false}
          disabledDate={disabledDate}
          disabledTime={disabledDateTime}
          format="DD/MM/YYYY HH:mm"
          placeholder="Data de devolução"
          size="large"
          style={{ width: "100%" }}
          locale={locale}
        />
      </Form.Item>

      <Form.Item
        name="hasDamageOrMissingItems"
        valuePropName="checked"
        label="O veículo possui avarias ou itens faltantes?"
        wrapperCol={{ span: 2 }}
        initialValue={false}
      >
        <Switch checkedChildren="Sim" unCheckedChildren= "Não" />
      </Form.Item>

      <Form.Item label="">
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <KintoButton theme={KintoButtonThemes.outlined} onClick={props.handleReturn}>Voltar</KintoButton>
          <KintoButton theme={KintoButtonThemes.contained} htmlType="submit">Próximo</KintoButton>
        </div>
      </Form.Item>
    </Form>
  )
}

export default CarInfo
